import type { SceneGroup } from '@lumen5/luminary';

import { partitionTranscriptByScene } from './transcript-partition';

import type { RootState } from 'js/src/libs/RootState';
import { isAIAvatarSceneGroup, isVoiceoverSceneGroup } from 'js/src/luminary/luminary-selectors';

const MIN_GAP_DURATION_MILLIS = 10;

export interface UncoveredTranscriptRange {
    startMillis: number;
    endMillis: number;
    words: string[];
}

/**
 * Finds transcript time ranges in a voiceover scene group that are not covered
 * by an overlay scene. In a voice-driven scene group, scenes tile the timeline,
 * so every non-overlay (base) scene is an uncovered range where the waveform
 * base is visible.
 *
 * Returns null when coverage does not apply: talking-head and AI-avatar scene
 * groups (their base visuals are fine to show) and groups without a transcript.
 */
export function getUncoveredTranscriptRanges(
    state: RootState,
    sceneGroup: SceneGroup,
): UncoveredTranscriptRange[] | null {
    if (!isVoiceoverSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
        return null;
    }
    const partition = partitionTranscriptByScene(state, sceneGroup);
    if (!partition) {
        return null;
    }

    // A coverage gap is simply a base ("uncovered") scene in the partition. Skip sub-10ms slivers
    // that arise from floating-point noise between adjacent overlays — but only when the sliver owns
    // no transcript words.
    return partition
        .filter((entry) => entry.role === 'uncovered')
        .filter(
            (entry) =>
                entry.words.length > 0 ||
                entry.endMillis - entry.startMillis >= MIN_GAP_DURATION_MILLIS,
        )
        .map((entry) => ({
            startMillis: entry.startMillis,
            endMillis: entry.endMillis,
            words: entry.words,
        }));
}

export function formatUncoveredRanges(ranges: UncoveredTranscriptRange[]): string {
    if (ranges.length === 0) {
        return 'Full transcript coverage — no gaps remain.';
    }
    const lines = ranges.map((range) => {
        const text = range.words.length > 0 ? `"${range.words.join(' ')}"` : '(no spoken words)';
        const start = (range.startMillis / 1000).toFixed(1);
        const end = (range.endMillis / 1000).toFixed(1);
        return `  - ${text} (${start}s–${end}s)`;
    });
    return `Uncovered transcript ranges remain — the viewer sees a bare waveform placeholder here instead which will look bad.\n${lines.join('\n')}`;
}
